import type { AiRecipe, AiRecipeNutrition } from "../types";

export interface RecipeValidationResult {
  valid: boolean;
  recipe: AiRecipe | null;
  errors: string[];
}

function isFiniteNonNeg(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function isFinitePositiveInt(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 && Number.isInteger(v);
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function validateNutrition(n: unknown): AiRecipeNutrition | null {
  if (typeof n !== "object" || n === null) return null;
  const obj = n as Record<string, unknown>;
  const result: AiRecipeNutrition = {
    calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0, sodium: 0,
  };
  const requiredFields: (keyof AiRecipeNutrition)[] = ["calories", "protein", "carbs", "fat", "fiber", "sodium"];
  for (const f of requiredFields) {
    if (!isFiniteNonNeg(obj[f])) return null;
    result[f] = obj[f] as number;
  }
  // Sugar is special: null/undefined/NaN means "unknown" — keep as null, do NOT coerce to 0
  const sugarVal = obj.sugar;
  if (sugarVal === null || sugarVal === undefined) {
    result.sugar = null as unknown as number;
  } else if (isFiniteNonNeg(sugarVal)) {
    result.sugar = sugarVal;
  } else {
    return null;
  }
  return result;
}

export function validateRecipe(raw: unknown): RecipeValidationResult {
  const errors: string[] = [];
  if (typeof raw !== "object" || raw === null) {
    return { valid: false, recipe: null, errors: ["Response is not an object"] };
  }
  const obj = raw as Record<string, unknown>;

  if (!isNonEmptyString(obj.name)) errors.push("Missing or empty name");
  if (!isFinitePositiveInt(obj.time)) errors.push("Missing or invalid time");
  if (!isFinitePositiveInt(obj.servings)) errors.push("Missing or invalid servings");

  let ingredientsList: AiRecipe["ingredients_list"] | undefined;
  if (Array.isArray(obj.ingredients_list)) {
    ingredientsList = [];
    for (const ing of obj.ingredients_list) {
      if (typeof ing !== "object" || ing === null) { errors.push("Invalid ingredient entry"); continue; }
      const i = ing as Record<string, unknown>;
      if (!isNonEmptyString(i.name)) { errors.push("Ingredient missing name"); continue; }
      ingredientsList.push({ name: i.name, quantity: typeof i.quantity === "string" ? i.quantity : String(i.quantity ?? "") });
    }
  } else {
    errors.push("Missing ingredients_list");
  }

  let steps: string[] | undefined;
  if (Array.isArray(obj.steps)) {
    steps = obj.steps.filter((s): s is string => isNonEmptyString(s));
    if (steps.length === 0) errors.push("No valid steps");
  } else {
    errors.push("Missing steps");
  }

  const nutrition = validateNutrition(obj.nutrition);
  if (!nutrition) errors.push("Missing or invalid nutrition");

  if (errors.length > 0) {
    return { valid: false, recipe: null, errors };
  }

  const recipe: AiRecipe = {
    name: obj.name as string,
    time: obj.time as number,
    servings: obj.servings as number,
    ingredients_list: ingredientsList!,
    steps: steps!,
    nutrition: nutrition!,
    cached: typeof obj.cached === "boolean" ? obj.cached : undefined,
  };

  return { valid: true, recipe, errors: [] };
}

export interface CacheMatchParams {
  requestedServings: number;
  requestedLang: string;
  requestedDietFilter?: string;
  cachedRecipe: AiRecipe;
}

export function cacheMatchesRequest(params: CacheMatchParams): boolean {
  const { requestedServings, requestedDietFilter, cachedRecipe } = params;
  if (cachedRecipe.servings !== requestedServings) return false;
  // Diet filter: if a diet was requested, the cached recipe must have been generated
  // with the same diet. We cannot verify this from the recipe data alone (no diet field
  // in AiRecipe), so when a diet filter is active, we reject cached recipes that lack
  // explicit diet metadata. This prevents serving a non-vegan recipe to a vegan request.
  if (requestedDietFilter && requestedDietFilter !== "none" && !cachedRecipe.dietFilter) return false;
  if (cachedRecipe.dietFilter && requestedDietFilter && cachedRecipe.dietFilter !== requestedDietFilter) return false;
  return true;
}

export function isKnownZeroSugar(recipe: AiRecipe): boolean {
  return recipe.nutrition != null && recipe.nutrition.sugar === 0;
}

export function isUnknownSugar(recipe: AiRecipe): boolean {
  return recipe.nutrition == null || recipe.nutrition.sugar == null || !Number.isFinite(recipe.nutrition.sugar);
}
